/**
 * R9 Assessment Area / REMA Matrix (Wallace v1, CLARID-SPEC-R9-001)
 *
 * One block per assessment area: the counties in/around the delineated AA with their
 * demographics (tracts / MMCTs for the current and prior year) and the bank's HMDA
 * lending concentration, plus a per-AA TOTAL row. The point of the screen is the FLAG:
 * a county OUTSIDE the delineated AA carrying ≥ 8.00% of the AA-block's applications is
 * a candidate REMA (Reasonably Expected Market Area) addition.
 *
 * Formatting encodes meaning (the workbook used UPPER CASE / italics / red):
 *   - delineated → "Delineated" badge (inside the AA); else "Outside AA"
 *   - hasMmct    → red county name (county contains ≥ 1 MMCT)
 *   - flagged    → highlighted row + Observation (REMA candidate)
 *   - pct null   → "NA" (county has 0 apps, or the AA-block total is 0)
 * Fair Lending screen → 2 decimal places.
 */

import clsx from 'clsx';

interface R9CountyRow {
  countyFips: string;
  countyName: string;
  delineated: boolean;
  hasMmct: boolean;
  cts2025: number;
  mmct2025: number;
  cts2024: number;
  mmct2024: number;
  apps2025: number;
  pct2025: number | null;
  apps2024: number;
  pct2024: number | null;
  flagged: boolean;
  observation?: string;
  isTotal?: boolean;
}

interface R9AaBlock {
  assessmentAreaName: string;
  cbsaType: 'MSA' | 'MD' | 'NONMSA';
  rows: R9CountyRow[];
  total: R9CountyRow;
  /** Absent on analyses stored before 2026-10-19 (shown as 2025 / 2024). */
  currentYear?: number;
  /** null = the prior year was not evaluated (no LAR or no AA built for it): its columns are hidden. */
  priorYear?: number | null;
}

interface R9MatrixProps {
  blocks: R9AaBlock[];
}

/** Render a share as "12.34%" or "NA" when null (#DIV/0! / zero volume). */
function pct(value: number | null): string {
  return value === null ? 'NA' : `${value.toFixed(2)}%`;
}

function cbsaLabel(type: R9AaBlock['cbsaType']): string {
  return type === 'NONMSA' ? 'Non-MSA' : type;
}

function CountyCell({ row }: { row: R9CountyRow }) {
  if (row.isTotal) {
    return <span className="font-semibold text-ink">TOTAL</span>;
  }
  return (
    <span className="flex items-center gap-2">
      <span
        className={clsx(
          'font-medium',
          row.hasMmct ? 'text-red-600' : 'text-ink',
          row.delineated && 'uppercase tracking-wide',
        )}
        title={row.hasMmct ? 'County contains ≥ 1 MMCT' : undefined}
      >
        {row.countyName}
      </span>
      <span
        className={clsx(
          'rounded px-1.5 py-0.5 text-[10px] font-medium uppercase',
          row.delineated ? 'bg-teal-50 text-teal-700' : 'bg-amber-50 text-amber-700',
        )}
      >
        {row.delineated ? 'Delineated' : 'Outside AA'}
      </span>
    </span>
  );
}

function AaBlock({ block }: { block: R9AaBlock }) {
  const allRows = [...block.rows, block.total];
  // Michael Wallace (2026-10-18): only years actually evaluated. Older stored blocks show both years.
  const year = block.currentYear ?? 2025;
  const prior = block.priorYear === undefined ? year - 1 : block.priorYear;
  const showPrior = prior !== null;
  const yy = (y: number | null) => String(y ?? '').slice(2);
  const flaggedCount = block.rows.filter((r) => r.flagged).length;

  return (
    <div className="mb-6">
      <div className="mb-2 flex items-center gap-2">
        <h4 className="text-sm font-semibold text-ink">{block.assessmentAreaName}</h4>
        <span className="rounded bg-panel-2 px-1.5 py-0.5 text-[10px] font-medium uppercase text-ink-3">
          {cbsaLabel(block.cbsaType)}
        </span>
        {flaggedCount > 0 && (
          <span className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-medium uppercase text-red-700">
            {flaggedCount} REMA candidate{flaggedCount === 1 ? '' : 's'}
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-panel-2 text-left text-xs font-medium uppercase tracking-wide text-ink-3">
              <th className="sticky left-0 z-10 bg-panel-2 px-3 py-2">County</th>
              <th className="px-3 py-2 text-right"># CTs &rsquo;{yy(year)}</th>
              <th className="px-3 py-2 text-right">MMCT &rsquo;{yy(year)}</th>
              {showPrior && <th className="px-3 py-2 text-right"># CTs &rsquo;{yy(prior)}</th>}
              {showPrior && <th className="px-3 py-2 text-right">MMCT &rsquo;{yy(prior)}</th>}
              <th className="px-3 py-2 text-right">Apps &rsquo;{yy(year)}</th>
              <th className="px-3 py-2 text-right">%</th>
              {showPrior && <th className="px-3 py-2 text-right">Apps &rsquo;{yy(prior)}</th>}
              {showPrior && <th className="px-3 py-2 text-right">%</th>}
              <th className="px-3 py-2">Observations</th>
            </tr>
          </thead>
          <tbody>
            {allRows.map((row) => (
              <tr
                key={row.isTotal ? 'TOTAL' : row.countyFips}
                className={clsx(
                  'border-b border-rule',
                  row.isTotal
                    ? 'bg-panel-2 font-semibold'
                    : row.flagged
                      ? 'bg-red-50/60 hover:bg-red-50'
                      : 'hover:bg-panel-2',
                )}
              >
                <td
                  className={clsx(
                    'sticky left-0 z-10 px-3 py-2.5',
                    row.isTotal ? 'bg-panel-2' : row.flagged ? 'bg-red-50/60' : 'bg-panel',
                  )}
                >
                  <CountyCell row={row} />
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-ink-3">{row.cts2025.toLocaleString()}</td>
                <td className={clsx('px-3 py-2.5 text-right tabular-nums', row.mmct2025 > 0 ? 'text-red-600' : 'text-ink-3')}>{row.mmct2025.toLocaleString()}</td>
                {showPrior && <td className="px-3 py-2.5 text-right tabular-nums text-ink-3">{row.cts2024.toLocaleString()}</td>}
                {showPrior && <td className={clsx('px-3 py-2.5 text-right tabular-nums', row.mmct2024 > 0 ? 'text-red-600' : 'text-ink-3')}>{row.mmct2024.toLocaleString()}</td>}
                <td className="px-3 py-2.5 text-right tabular-nums text-ink-2">{row.apps2025.toLocaleString()}</td>
                <td className={clsx('px-3 py-2.5 text-right tabular-nums', row.flagged ? 'font-semibold text-red-700' : 'text-ink-3')}>{pct(row.pct2025)}</td>
                {showPrior && <td className="px-3 py-2.5 text-right tabular-nums text-ink-2">{row.apps2024.toLocaleString()}</td>}
                {showPrior && <td className={clsx('px-3 py-2.5 text-right tabular-nums', row.flagged ? 'font-semibold text-red-700' : 'text-ink-3')}>{pct(row.pct2024)}</td>}
                <td className="px-3 py-2.5 text-xs text-ink-3">{row.observation ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function R9Matrix({ blocks }: R9MatrixProps) {
  if (blocks.length === 0) {
    return (
      <div className="rounded border border-dashed border-rule p-8 text-center">
        <p className="text-sm text-ink-3">No assessment-area data available for R9.</p>
        <p className="mt-1 text-xs text-ink-3">
          Define the bank&rsquo;s assessment areas (and ingest the relevant FFIEC census years) to enable the
          AA / REMA analysis.
        </p>
      </div>
    );
  }

  return (
    <div>
      {blocks.map((block) => (
        <AaBlock key={block.assessmentAreaName} block={block} />
      ))}

      <div className="mt-1 text-xs text-ink-3">
        <span className="font-medium uppercase tracking-wide">Delineated</span> = county inside the bank&rsquo;s
        assessment area; <span className="font-medium">Outside AA</span> counties with{' '}
        <span className="font-medium text-red-700">≥ 8.00%</span> of the area&rsquo;s applications are flagged as
        candidate REMA additions (FRB concentration threshold). A{' '}
        <span className="font-medium text-red-600">red</span> county name or MMCT count means the county contains
        at least one MMCT. &quot;NA&quot; = no applications (or no block volume) that year.
      </div>
    </div>
  );
}
